import React from 'react';
import { Stack, Text, Button, Group, ThemeIcon, Paper, Container } from '@mantine/core';
import { IconLock, IconArrowLeft, IconLogin } from '@tabler/icons-react';
import { AccessDeniedMessageProps } from './AccessDeniedMessage.types';

/**
 * AccessDeniedMessage Component
 *
 * Displays a user-friendly message when access to a resource is denied
 *
 * Features:
 * - Clear unauthorized access message
 * - Resource-specific messaging
 * - Required role display
 * - Action buttons (back, login)
 * - Accessible and responsive
 *
 * Usage:
 * ```tsx
 * <AccessDeniedMessage
 *   resource="Admin Dashboard"
 *   requiredRole="Admin"
 *   showBackButton
 *   onBack={() => navigate(-1)}
 * />
 * ```
 */
export const AccessDeniedMessage: React.FC<AccessDeniedMessageProps> = ({
  message,
  resource = 'this page',
  requiredRole,
  showBackButton = true,
  onBack,
  showLoginButton = false,
  onLogin
}): React.ReactElement => {
  // Default message
  const defaultMessage = requiredRole
    ? `You do not have permission to access ${resource}. This resource requires ${requiredRole} access.`
    : `You do not have permission to access ${resource}.`;

  const displayMessage = message || defaultMessage;

  return (
    <Container size="sm" py="xl">
      <Paper shadow="md" p="xl" radius="md" withBorder>
        <Stack align="center" gap="lg">
          {/* Icon */}
          <ThemeIcon size={80} radius="xl" variant="light" color="red">
            <IconLock size={40} />
          </ThemeIcon>

          {/* Title */}
          <Stack align="center" gap="xs">
            <Text size="xl" fw={700} ta="center">
              Access Denied
            </Text>
            <Text size="sm" c="dimmed" ta="center" maw={400}>
              {displayMessage}
            </Text>
          </Stack>

          {/* Required Role Badge */}
          {requiredRole && (
            <Paper bg="gray.1" p="sm" radius="md">
              <Text size="sm" c="dimmed" ta="center">
                <strong>Required role:</strong> {requiredRole}
              </Text>
            </Paper>
          )}

          {/* Actions */}
          <Group justify="center" gap="sm" mt="md">
            {showBackButton && (
              <Button
                leftSection={<IconArrowLeft size={16} />}
                variant="light"
                color="gray"
                onClick={onBack}
              >
                Go Back
              </Button>
            )}

            {showLoginButton && onLogin && (
              <Button leftSection={<IconLogin size={16} />} variant="filled" onClick={onLogin}>
                Login
              </Button>
            )}
          </Group>

          {/* Help Text */}
          <Text size="xs" c="dimmed" ta="center" mt="md">
            If you believe you should have access to this resource, please contact your
            administrator.
          </Text>
        </Stack>
      </Paper>
    </Container>
  );
};
